import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import type {
	CustomerInput,
	CustomerOutput,
	UpdateCustomerBody,
} from '@/types/customer'
import type { OrgBranch } from '@/types/organization'

export interface CustomerFormProps {
	mode: 'create' | 'edit'
	initial?: CustomerOutput
	branches: OrgBranch[]
	onSave: (data: CustomerInput | UpdateCustomerBody) => void
	onClose: () => void
}

export function CustomerFormContent({
	mode,
	initial,
	branches,
	onSave,
	onClose,
}: CustomerFormProps) {
	const { t } = useTranslation()

	const [name, setName] = useState(initial?.name ?? '')
	const [crId, setCrId] = useState(initial?.cr.id ?? '')

	const availableCrs = useMemo(
		() =>
			branches.flatMap((branch) =>
				branch.crs.map((cr) => ({
					value: cr.id,
					label: `${branch.name} - ${cr.name}`,
				})),
			),
		[branches],
	)

	const handleSave = () => {
		const trimmedName = name.trim()
		if (trimmedName.length < 2) {
			toast.error(t('administration.customers.form.invalidNameError'))
			return
		}

		// In edit mode, only send the name
		if (mode === 'edit') {
			onSave({ name: trimmedName })
			return
		}

		if (!crId) {
			toast.error(t('administration.customers.form.emptyContractError'))
			return
		}

		onSave({
			name: trimmedName,
			crId,
		})
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="customer-name">
					{t('administration.customers.form.nameLabel')}
				</FieldLabel>
				<FieldContent>
					<Input
						id="customer-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t('administration.customers.form.namePlaceholder')}
						maxLength={120}
					/>
				</FieldContent>
			</Field>

			<Field>
				<FieldLabel htmlFor="customer-cr">
					{t('administration.customers.form.selectContract')}
				</FieldLabel>
				<FieldContent>
					<SearchableSelect
						id="customer-cr"
						value={crId}
						onValueChange={setCrId}
						disabled={mode === 'edit'}
						options={availableCrs}
						placeholder={t(
							'administration.customers.form.selectContractPlaceholder',
						)}
						searchPlaceholder={t('common.search')}
						emptyMessage={t('administration.customers.form.noContractsFound')}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
				>
					{t('administration.customers.form.cancelButton')}
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{mode === 'create'
						? t('administration.customers.form.saveButton')
						: t('administration.customers.form.saveEditButton')}
				</Button>
			</div>
		</div>
	)
}
